// src/components/common/LoadingScreen.tsx

import { memo, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Heart } from "lucide-react";
import logo from "../../assets/img/logo.png";
import { APP_CONFIG } from "../../config/app.config";
import { useCampaign } from "../../campaigns/providers/CampaignProvider";

export const LoadingScreen = memo(({ onFinished }: { onFinished?: () => void }) => {
  const [progress, setProgress] = useState(0);
  const [lineIndex, setLineIndex] = useState(0);
  const { activeCampaign } = useCampaign();

  const messages = useMemo<readonly string[]>(() => {
    return activeCampaign?.copy.loadingMessages?.length
      ? activeCampaign.copy.loadingMessages
      : APP_CONFIG.loading.messages;
  }, [activeCampaign?.copy.loadingMessages]);

  useEffect(() => {
    const progressTimer = window.setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          window.clearInterval(progressTimer);
          window.setTimeout(() => onFinished?.(), 900);
          return 100;
        }

        const increment =
          prev < 60 ? Math.random() * 4 + 2 : prev < 85 ? Math.random() * 1.4 + 0.6 : 0.15;

        return Math.min(100, prev + increment);
      });
    }, APP_CONFIG.loading.progressIncrementInterval);

    const lineTimer = window.setInterval(() => {
      setLineIndex((prev) => (prev + 1) % messages.length);
    }, 2600);

    return () => {
      window.clearInterval(progressTimer);
      window.clearInterval(lineTimer);
    };
  }, [onFinished, messages.length]);

  return (
    <div className="fixed inset-0 z-[1000] bg-[#040507] flex items-center justify-center overflow-hidden text-white">
      <div className="absolute inset-0 bg-gradient-to-br from-[#040507] via-[#1c1c1c] to-[#040507]" />

      <motion.div
        animate={{
          opacity: [0.1, 0.2, 0.1],
        }}
        transition={{ duration: 4, repeat: Infinity }}
        className="absolute inset-0 bg-gradient-to-t from-transparent via-yellow-500/5 to-transparent"
      />

      <div className="relative z-10 flex flex-col items-center px-8 max-w-sm w-full">
        <Logo />

        <LoadingMessage index={lineIndex} messages={messages} />

        {progress > 82 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mt-4 text-[10px] uppercase tracking-[.4em] opacity-40"
          >
            Someone chose you.
          </motion.div>
        )}

        <ProgressBar progress={progress} />
      </div>

      <FloatingPetals />

      <Footer />
    </div>
  );
});

LoadingScreen.displayName = "LoadingScreen";

const Logo = memo(() => (
  <motion.img
    src={logo}
    alt="LRNSY"
    animate={{ scale: [1, 1.04, 1] }}
    transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
    className="w-24 md:w-32 mb-10 select-none pointer-events-none drop-shadow-[0_0_35px_rgba(255,255,255,0.08)]"
    loading="eager"
  />
));

Logo.displayName = "Logo";

const LoadingMessage = memo(
  ({ index, messages }: { index: number; messages: readonly string[] }) => (
    <div className="h-10 flex items-center justify-center">
      <AnimatePresence mode="wait">
        <motion.p
          key={`${index}-${messages[index]}`}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.45 }}
          className="uppercase text-[11px] font-black tracking-[.32em] text-white/70 text-center"
        >
          {messages[index]}
        </motion.p>
      </AnimatePresence>
    </div>
  )
);

LoadingMessage.displayName = "LoadingMessage";

const ProgressBar = memo(({ progress }: { progress: number }) => (
  <div className="w-full mt-12">
    <div className="flex justify-between text-[9px] uppercase tracking-[.3em] mb-2 opacity-50">
      <span>Preparing Experience</span>
      <span>{Math.floor(progress)}%</span>
    </div>

    <div className="h-[2px] w-full bg-white/10 overflow-hidden rounded-full relative">
      <motion.div
        animate={{ width: `${progress}%` }}
        transition={{ duration: 0.4 }}
        className="absolute inset-y-0 left-0 bg-white rounded-full"
      />
    </div>
  </div>
));

ProgressBar.displayName = "ProgressBar";

const FloatingPetals = memo(() => (
  <div className="absolute inset-0 pointer-events-none">
    {Array.from({ length: 6 }).map((_, i) => (
      <motion.div
        key={i}
        animate={{
          y: [0, -200],
          opacity: [0, 0.7, 0],
          x: [Math.random() * 160 - 80, Math.random() * 160 - 80],
        }}
        transition={{
          repeat: Infinity,
          duration: 6 + i,
          delay: i * 0.7,
        }}
        className="absolute bottom-0 left-1/2"
      >
        <Heart size={10} className="opacity-20" />
      </motion.div>
    ))}
  </div>
));

FloatingPetals.displayName = "FloatingPetals";

const Footer = memo(() => (
  <footer className="absolute bottom-8 w-full px-10 flex justify-between opacity-20 text-[8px] uppercase tracking-[.4em]">
    <span>LRNSY EXPERIENCE</span>
    <span>2026</span>
  </footer>
));

Footer.displayName = "Footer";
